const ROW_INSTRUMENTS = ['kick', 'snare', 'hat', 'perc', 'bass', 'lead'];
const ROW_HEX = ['#ff1e5a', '#ff6a1a', '#ffd60a', '#26e884', '#3d5cff', '#b64bff'];

export class Hud {
  private readonly scoreValue = this.getElement('#score-value');
  private readonly bricksValue = document.querySelector<HTMLElement>('#bricks-value');
  private readonly bricksTotal = document.querySelector<HTMLElement>('#bricks-total');
  private readonly livesValue = this.getElement('#lives-value');
  private readonly bpmValue = this.getElement('#bpm-value');
  private readonly beatPips = Array.from(document.querySelectorAll<HTMLElement>('#beat-pips i'));
  private readonly statusLine = this.getElement('#status-line');
  private readonly sampleCount = document.querySelector<HTMLElement>('#sample-count');
  private readonly sampleList = this.getElement('#sample-list');
  private readonly app = this.getElement('#app');
  private readonly seqCells: HTMLElement[][] = [];
  private lastPlayhead = -1;

  private rowLabelEls: HTMLButtonElement[] = [];
  private onRowLabelClick: ((row: number, mods: { shift: boolean; alt: boolean; ctrl: boolean }) => void) | null = null;
  private onSampleDropped: ((row: number, sampleId: string) => void) | null = null;
  private onSamplePlay: ((sampleId: string) => void) | null = null;
  private onCellClick: ((row: number, step: number, mods: { shift: boolean }) => void) | null = null;
  private onCellPaint: ((row: number, step: number, mode: 'arm' | 'clear') => void) | null = null;
  private onLockClick: ((row: number) => void) | null = null;
  private rowLockEls: HTMLElement[] = [];
  /** Active brush during a click-and-drag paint gesture. Set on pointerdown
   *  (based on the initial cell's state), cleared on pointerup anywhere. */
  private brushMode: 'arm' | 'clear' | null = null;
  /** Cells already touched by the current brush stroke — prevents re-firing
   *  on the same cell as the pointer wiggles inside its bounds. */
  private brushedCells: Set<string> = new Set();

  constructor() {
    const seqGrid = document.querySelector<HTMLElement>('#sequencer-grid');
    if (seqGrid) {
      seqGrid.innerHTML = '';
      for (let row = 0; row < 6; row++) {
        const rowEl = document.createElement('div');
        rowEl.className = 'seq-row';
        const label = document.createElement('button');
        label.type = 'button';
        label.className = 'seq-label';
        label.textContent = ROW_INSTRUMENTS[row] ?? '';
        label.title = 'Click to preview · ctrl-click to lock this slice · shift-click to clear · alt-click to load';
        label.style.setProperty('--row-color', ROW_HEX[row] ?? '#7cf0c2');
        label.addEventListener('click', (ev) => {
          this.onRowLabelClick?.(row, {
            shift: ev.shiftKey,
            alt: ev.altKey,
            ctrl: ev.ctrlKey || ev.metaKey,
          });
        });
        // Right-click on the label = shortcut for lock toggle so the player
        // can flip locks mid-game without hunting for the badge.
        label.addEventListener('contextmenu', (ev) => {
          ev.preventDefault();
          this.onLockClick?.(row);
        });
        label.addEventListener('dragover', (ev) => {
          ev.preventDefault();
          if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
          label.classList.add('drop-target');
        });
        label.addEventListener('dragleave', () => label.classList.remove('drop-target'));
        label.addEventListener('drop', (ev) => {
          ev.preventDefault();
          label.classList.remove('drop-target');
          const id = ev.dataTransfer?.getData('text/plain');
          if (id) this.onSampleDropped?.(row, id);
        });
        // Real clickable lock badge (was a ::before pseudo, which swallowed
        // clicks up to the parent button and triggered preview instead of
        // toggling the lock). Only visible in sample mode via CSS.
        const lock = document.createElement('span');
        lock.className = 'seq-lock';
        lock.setAttribute('aria-label', 'Lock this slice');
        lock.title = 'Click to lock this slice · locked rows keep their slice across reshuffles';
        lock.addEventListener('click', (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          this.onLockClick?.(row);
        });
        // Prevent the mousedown on the lock from also arming the parent
        // button's active/press state.
        lock.addEventListener('mousedown', (ev) => ev.stopPropagation());
        label.append(lock);
        this.rowLockEls[row] = lock;
        this.rowLabelEls[row] = label;
        rowEl.append(label);
        this.seqCells[row] = [];
        for (let step = 0; step < 16; step++) {
          const cell = document.createElement('div');
          cell.className = 'seq-cell';
          if (step % 4 === 0) cell.classList.add('beat');
          cell.style.setProperty('--row-color', ROW_HEX[row] ?? '#7cf0c2');
          // Data attrs so container-level pointermove can identify which
          // cell the drag is over via elementFromPoint hit-testing.
          cell.dataset.row = String(row);
          cell.dataset.step = String(step);
          // Manual note toggle — user can compose a static beat that plays
          // forever alongside brick-armed notes. Fires on pointerdown (not
          // click) so touches / quick drags don't get eaten by the click
          // gesture rule (mousedown+up on the same element without moving).
          const stepRef = step;
          const rowRef = row;
          cell.addEventListener('pointerdown', (ev) => {
            ev.preventDefault();
            const wasArmed = cell.classList.contains('armed');
            this.onCellClick?.(rowRef, stepRef, { shift: ev.shiftKey });
            // Brush mode = opposite of initial state so dragging over more
            // empty cells arms them, and dragging over more armed cells
            // clears them — never toggles both ways in a single stroke.
            this.brushMode = wasArmed ? 'clear' : 'arm';
            this.brushedCells.clear();
            this.brushedCells.add(`${rowRef}:${stepRef}`);
          });
          rowEl.append(cell);
          this.seqCells[row]!.push(cell);
        }
        seqGrid.append(rowEl);
      }

      // Drag-paint: while brush mode is active, hit-test the cell under the
      // pointer and apply the brush. Works for touch too — pointermove +
      // elementFromPoint bypasses the "touch stays on original element"
      // rule that would otherwise stop paint at the first cell.
      seqGrid.addEventListener('pointermove', (ev) => {
        if (this.brushMode === null || ev.buttons === 0) return;
        const target = document.elementFromPoint(ev.clientX, ev.clientY);
        if (!(target instanceof HTMLElement)) return;
        const cell = target.closest<HTMLElement>('.seq-cell');
        if (!cell || cell.dataset.row === undefined || cell.dataset.step === undefined) return;
        const r = Number(cell.dataset.row);
        const s = Number(cell.dataset.step);
        const key = `${r}:${s}`;
        if (this.brushedCells.has(key)) return;
        this.brushedCells.add(key);
        this.onCellPaint?.(r, s, this.brushMode);
      });
      // End the stroke anywhere the pointer lifts — inside or outside the
      // grid. Also handles right-click / gesture cancellation.
      const endBrush = () => {
        this.brushMode = null;
        this.brushedCells.clear();
      };
      window.addEventListener('pointerup', endBrush);
      window.addEventListener('pointercancel', endBrush);
    }
  }

  onRowLabelClicked(cb: (row: number, mods: { shift: boolean; alt: boolean; ctrl: boolean }) => void): void {
    this.onRowLabelClick = cb;
  }

  onSampleDroppedOnRow(cb: (row: number, sampleId: string) => void): void {
    this.onSampleDropped = cb;
  }

  onSamplePlayClicked(cb: (sampleId: string) => void): void {
    this.onSamplePlay = cb;
  }

  /** Reflect which sample is currently previewing — that row's ▶ button flips
   *  to ⏹ and gets the .is-playing class; passing null clears all buttons. */
  setSamplePreviewPlaying(sampleId: string | null): void {
    if (!this.sampleList) return;
    const items = this.sampleList.querySelectorAll<HTMLLIElement>('li[data-sample-id]');
    items.forEach((li) => {
      const play = li.querySelector<HTMLButtonElement>('.sample-play');
      if (!play) return;
      const active = li.dataset.sampleId === sampleId;
      play.textContent = active ? '⏹' : '▶';
      play.classList.toggle('is-playing', active);
    });
  }

  onCellClicked(cb: (row: number, step: number, mods: { shift: boolean }) => void): void {
    this.onCellClick = cb;
  }

  /** Fires for each cell dragged over during a paint stroke (after the
   *  initial pointerdown). Mode is fixed for the whole stroke: 'arm' only
   *  fills empty cells, 'clear' only removes armed cells. */
  onCellPainted(cb: (row: number, step: number, mode: 'arm' | 'clear') => void): void {
    this.onCellPaint = cb;
  }

  onLockClicked(cb: (row: number) => void): void {
    this.onLockClick = cb;
  }

  setRowSlot(row: number, name: string | null): void {
    const el = this.rowLabelEls[row];
    if (!el) return;
    el.classList.toggle('has-slot', !!name);
    if (name) {
      const trimmed = name.length > 8 ? `${name.slice(0, 7)}…` : name;
      el.textContent = trimmed;
      el.title = `${name} (click to change · shift-click to clear)`;
    } else {
      // Restored below via setSeqRowLabels
    }
  }

  /** Override the row's --row-color custom property so armed cells + the
   *  padlock badge tint to the currently-assigned slice color. The label
   *  itself stays neutral until the row is `.locked` — CSS in .locked
   *  picks up --row-color from here for the outline/bg. */
  setRowColor(row: number, color: string | null): void {
    const el = this.rowLabelEls[row];
    if (!el) return;
    // Don't set inline text color — .locked class + CSS handle the "row
    // adopts slice color" visual. Keeps unlocked labels reading as neutral.
    el.style.removeProperty('color');
    const rowColor = color ?? ROW_HEX[row] ?? '#7cf0c2';
    el.style.setProperty('--row-color', rowColor);
    for (const cell of this.seqCells[row] ?? []) {
      cell.style.setProperty('--row-color', rowColor);
    }
  }

  setSeqCellArmed(row: number, step: number, armed: boolean): void {
    const cell = this.seqCells[row]?.[step];
    if (!cell) return;
    cell.classList.toggle('armed', armed);
  }

  /** Visually mark a row label as "locked" so the user knows its slice won't
   *  rotate on the per-measure reshuffle. Toggled from Game via ctrl-click.
   *  Also tags the parent .seq-row so CSS can wash the whole row grey — reads
   *  as "this row is a custom-authored track, don't touch it". */
  setRowLocked(row: number, locked: boolean): void {
    const el = this.rowLabelEls[row];
    if (!el) return;
    el.classList.toggle('locked', locked);
    const rowEl = el.parentElement;
    rowEl?.classList.toggle('locked-row', locked);
    el.title = locked
      ? 'LOCKED · click badge or right-click to unlock · shift-click to clear · alt-click to load'
      : 'Click badge or right-click to lock this row · click label to preview · shift-click to clear · alt-click to load';
  }

  /** Flip the whole sequencer grid into "sample mode" styling. Currently just
   *  reveals the always-visible dim padlock badge on every row label so users
   *  discover the ctrl-click-to-lock gesture without hunting through titles. */
  setSampleMode(on: boolean): void {
    const grid = document.querySelector<HTMLElement>('#sequencer-grid');
    grid?.classList.toggle('sample-mode', on);
  }

  setSeqPlayhead(step: number): void {
    if (this.lastPlayhead === step) return;
    for (const row of this.seqCells) {
      row[this.lastPlayhead]?.classList.remove('playhead');
      row[step]?.classList.add('playhead');
    }
    this.lastPlayhead = step;
  }

  clearSeqCells(): void {
    for (const row of this.seqCells) {
      for (const cell of row) {
        cell.classList.remove('armed');
        cell.classList.remove('playhead');
      }
    }
    this.lastPlayhead = -1;
  }

  flashSeqCell(row: number, step: number): void {
    const cell = this.seqCells[row]?.[step];
    if (!cell) return;
    cell.classList.remove('flash');
    void cell.offsetWidth;
    cell.classList.add('flash');
  }

  setSeqRowLabels(labels: readonly string[]): void {
    const rows = document.querySelectorAll<HTMLElement>('.seq-row .seq-label');
    rows.forEach((el, i) => {
      const label = labels[i];
      if (label) el.textContent = label;
    });
  }

  update(state: {
    score: number;
    bricksLeft: number;
    bricksTotal: number;
    lives: number;
    bpm: number;
    beatInBar: number;
    status: string;
  }): void {
    this.scoreValue.textContent = String(state.score);
    if (this.bricksValue) this.bricksValue.textContent = String(state.bricksLeft);
    if (this.bricksTotal) this.bricksTotal.textContent = String(state.bricksTotal);
    this.livesValue.textContent = String(state.lives);
    this.bpmValue.textContent = String(Math.round(state.bpm));
    this.statusLine.textContent = state.status;

    this.beatPips.forEach((pip, index) => {
      const active = index === state.beatInBar;
      pip.classList.toggle('on', active);
      pip.classList.toggle('down', active && index === 0);
    });
  }

  pulseBeat(downbeat: boolean): void {
    this.app.classList.remove('beat-flash');
    // Force reflow so rapid beats still flash.
    void this.app.offsetWidth;
    this.app.classList.add('beat-flash');
    if (downbeat) {
      this.bpmValue.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }],
        { duration: 120, easing: 'ease-out' },
      );
    }
  }

  flash(kind: 'hit' | 'loop' | 'life' | 'win' | 'load' = 'hit'): void {
    const colors: Record<typeof kind, string> = {
      hit: '#7cf0c2',
      loop: '#7a9cff',
      life: '#ff8b6a',
      win: '#f5ba49',
      load: '#eef2ff',
    };
    this.statusLine.animate(
      [
        { transform: 'translateY(0)', borderLeftColor: colors[kind] },
        { transform: 'translateY(-3px)', borderLeftColor: colors[kind] },
        { transform: 'translateY(0)', borderLeftColor: colors[kind] },
      ],
      { duration: 220, easing: 'ease-out' },
    );
  }

  renderSamples(
    samples: Array<{ id: string; name: string; isLoopCandidate: boolean; source: string }>,
    loopingIds: Set<string>,
    mode: 'system' | 'folder' | 'sample',
    activeName: string | null,
  ): void {
    if (this.sampleCount) this.sampleCount.textContent = mode;
    const activeEl = document.querySelector<HTMLElement>('#active-sample');
    if (activeEl) activeEl.textContent = activeName ?? (mode === 'folder' ? 'drop a file' : 'no sound');
    const buttons = document.querySelectorAll<HTMLButtonElement>('.mode-btn');
    buttons.forEach((btn) => btn.classList.toggle('on', btn.dataset.mode === mode));

    this.sampleList.innerHTML = '';
    // Only show samples that match the active mode.
    const visible = samples.filter((s) =>
      mode === 'system'
        ? s.source === 'procedural'
        : mode === 'sample'
          ? s.source === 'chop'
          : s.source === 'folder' || s.source === 'drop',
    );
    for (const sample of visible) {
      const li = document.createElement('li');
      // Always tag every row with its sample id so preview state can find the
      // right button on toggle (system rows don't need drag/drop but do need
      // play/stop visuals).
      li.dataset.sampleId = sample.id;
      if (loopingIds.has(sample.id)) li.classList.add('looping');
      // Folder + chop samples can be dragged onto sequencer row labels to pin
      // them as sound slots.
      if (mode === 'folder' || mode === 'sample') {
        li.setAttribute('draggable', 'true');
        li.addEventListener('dragstart', (ev) => {
          ev.dataTransfer?.setData('text/plain', sample.id);
          if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'copy';
          li.classList.add('dragging');
        });
        li.addEventListener('dragend', () => li.classList.remove('dragging'));
      }
      const label = document.createElement('span');
      label.textContent = sample.name;
      const play = document.createElement('button');
      play.type = 'button';
      play.className = 'sample-play';
      play.setAttribute('aria-label', `Preview ${sample.name}`);
      play.title = 'Preview';
      play.textContent = '▶';
      play.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.onSamplePlay?.(sample.id);
      });
      li.append(label, play);
      this.sampleList.append(li);
    }
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
